import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "contexts/UserContext";

export const useSNAP = ({
  useToken = false,
  useUserId = true,
  pathUrl = "", // opcional, para producción o servidores externos
} = {}) => {
  const { user,refreshUserToken,logoutUser } = useUser();
  const navigate = useNavigate();

  const urlEndpointRef = useRef("");
  const paramsRef = useRef({});
  const bodyParamsRef = useRef({});
  const metodoRef = useRef("GET");
  const headers = { "Content-Type": "application/json" };

  const setFuncion = useCallback((funcion) => {
    urlEndpointRef.current = funcion.startsWith("/") ? funcion : `/${funcion}`;
  }, []);

  const setMetodo = useCallback((m) => {
    metodoRef.current = m.toUpperCase();
  }, []);

  const setParam = useCallback((key, value) => {
    paramsRef.current[key] = value;
  }, []);

  const setParamBody = useCallback((key, value) => {
    bodyParamsRef.current[key] = value;
  }, []);

  const prepareHeaders = useCallback(() => {
    const hdrs = { ...headers };
    if (useToken && user?.token) {
      hdrs["Authorization"] = `Bearer ${user.token}`;
    }
    return hdrs;
  }, [user, useToken]);

  const buildUrl = useCallback(() => {
    const endpoint = urlEndpointRef.current.startsWith("/")
      ? urlEndpointRef.current
      : `/${urlEndpointRef.current}`;

    let url;

    // 🔹 Desarrollo con proxy: si pathUrl incluye localhost, usamos ruta relativa
    const isDevProxy = process.env.NODE_ENV === "development" && !pathUrl;

    if (isDevProxy) {
      url = endpoint; // la ruta relativa pasará por el proxy
    } else if (!pathUrl) {
      const baseURL = process.env.REACT_APP_API_URL || "";
      url = new URL(window.location.origin + baseURL + endpoint).toString();
    } else {
      // Producción: pathUrl absoluto
      const normalizedPathUrl = pathUrl.endsWith("/")
        ? pathUrl.slice(0, -1)
        : pathUrl;
      url = normalizedPathUrl + endpoint;
    }

    // Construir query params manualmente para URL string
    const params = new URLSearchParams(paramsRef.current);
    if (useUserId && user?.id) params.append("user_id", user.id);

    const queryString = params.toString();

    if (queryString) {
      url += (url.includes("?") ? "&" : "?") + queryString;
    }

    return url;
  }, [user, useUserId, pathUrl]);

  const fetchData = useCallback(
    async (additionalBody = null, { reconnect = false } = {}) => {
      const url = buildUrl();
      const options = {
        method: metodoRef.current,
        headers: prepareHeaders(),
      };

      if (metodoRef.current === "POST") {
        const body = { ...bodyParamsRef.current, ...additionalBody };
        options.body = JSON.stringify(body);
      }

      try {

        //localStorage.setItem("lastActivity", Date.now().toString());

        const response = await fetch(url, options);
        const contentType = response.headers.get("Content-Type") || "";

        if (contentType.includes("application/json")) {
          const data = await response.json();

          if (response.status >= 500 && response.status < 600) {
            throw new Error(
              `Error del servidor: ${response.status} ${response.statusText}`
            );
          }

          if (response.status === 401 && data?.Error?.ID > 0) return data;

          // 🔹 Si la sesión caduca y reconnect está activado
          if (
            (response.status === 401 || response.status === 403) &&
            reconnect
          ) {
            console.warn("Sesión caducada, intentando reconexión...");

            const refreshed = await refreshUserToken();
            if (refreshed) {
              console.info(
                "Reautenticado correctamente, reintentando petición..."
              );
              return await fetchData(additionalBody, { reconnect: false }); // evita bucle infinito
            } else {
              console.error(
                "No se pudo renovar el token, redirigiendo al login..."
              );
              logoutUser();
              return null;
            }
          }

          if (response.status === 401 || response.status === 403) {
            logoutUser();
            throw new Error("Sesión caducada");
          }

          const rawToken = response.headers.get("Authorization");
          let token = null;
          if (rawToken?.startsWith("Bearer ")) token = rawToken.split(" ")[1];

          if (data?.Error?.NIVELL === 999) {
            alert("Error: " + data.Error?.TXT);
            logoutUser();
            return null;
          }

          return token ? { ...data, token } : data;
        }

        // Archivos / Blob
        if (
          contentType.includes("application/pdf") ||
          contentType.includes("image/") ||
          contentType.includes("application/vnd.ms-excel") ||
          contentType.includes(
            "application/vnd.openxmlformats-officedocument"
          ) ||
          contentType.includes("application/zip") ||
          contentType.startsWith("application/")
        ) {
          const blob = await response.blob();
          return { Blob: blob, ContentType: contentType };
        }

        return await response.text();
      } catch (err) {
        console.error("Error en la solicitud SNAP:", err);
        throw err;
      }
    },
    [buildUrl, prepareHeaders, navigate]
  );

  const crud = {
    get: async (endpoint, params = {}) => {
      setFuncion(endpoint);
      setMetodo("GET");
      Object.entries(params).forEach(([k, v]) => setParam(k, v));
      return fetchData();
    },
    post: async (endpoint, body = {}, params = {}) => {
      setFuncion(endpoint);
      setMetodo("POST");
      Object.entries(params).forEach(([k, v]) => setParam(k, v));
      Object.entries(body).forEach(([k, v]) => setParamBody(k, v));
      return fetchData();
    },
  };

  return {
    setFuncion,
    setMetodo,
    setParam,
    setParamBody,
    fetchData,
    ...crud,
  };
};
